import { useCallback, useEffect, useRef, useState } from 'react';

import { SOURCES, fetchAllSources } from './sources.js';

/**
 * Loads every source Worker in parallel and exposes per-source state.
 *
 * Sources land independently: `sourceStates` updates the moment each one settles, so a
 * slow or dead Worker never blocks the others from rendering. `loading` only reports
 * whether anything is still outstanding.
 */
export function useHackathons() {
  const [sourceStates, setSourceStates] = useState(() =>
    SOURCES.map((source) => ({
      id: source.id,
      label: source.label,
      status: 'loading',
      records: [],
      count: 0,
    })),
  );
  const [loading, setLoading] = useState(SOURCES.length > 0);
  const abortRef = useRef(null);

  const load = useCallback(() => {
    if (SOURCES.length === 0) {
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setSourceStates(
      SOURCES.map((source) => ({
        id: source.id,
        label: source.label,
        status: 'loading',
        records: [],
        count: 0,
      })),
    );

    fetchAllSources({
      signal: controller.signal,
      // Fires per source as it lands — this is what gives us incremental rendering.
      onSettled: (result) => {
        if (controller.signal.aborted) return;
        setSourceStates((previous) =>
          previous.map((state) => (state.id === result.id ? result : state)),
        );
      },
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
  }, []);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  // Only successful sources contribute records; failures are skipped silently.
  const records = sourceStates.flatMap((state) =>
    state.status === 'ok' ? state.records : [],
  );

  return { sourceStates, records, loading, reload: load };
}
